import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Calendar, Mail, Phone, Plus, Search } from 'lucide-react'
import { TopBar } from '@/components/layouts/TopBar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth-context'
import { formatCurrency } from '@/lib/format'
import { useClientSearchV2 } from '@/hooks/useClientSearchV2'
import { useDashboardHomeHeaderV2 } from '@/hooks/useDashboardHomeHeaderV2'
import { useDashboardSignedProjectsV2 } from '@/hooks/useDashboardSignedProjectsV2'
import { useDashboardInterventionsV2 } from '@/hooks/useDashboardInterventionsV2'
import { IncompleteProfileBanner } from '@/components/features/dashboard/IncompleteProfileBanner'
import { ArchivedSection } from '@/components/features/dashboard/sales-cockpit/ArchivedSection'
import { DeletedProjectsSection } from '@/components/features/project-detail/lifecycle/DeletedProjectsSection'
import { CommercialDiscussionCardIssued } from '@/components/features/dashboard/sales-cockpit/CommercialDiscussionCardIssued'
import { FollowUpDialog } from '@/components/features/dashboard/sales-cockpit/FollowUpDialog'
import { FollowUpHistoryDialog } from '@/components/features/dashboard/sales-cockpit/FollowUpHistoryDialog'
import { UrgencyBucketSection } from '@/components/features/dashboard/sales-cockpit/UrgencyBucketSection'
import { InterventionsSection } from '@/components/features/dashboard/sales-cockpit/InterventionsSection'
import { WinnerImportButton } from '@/components/features/winner-import/WinnerImportButton'
import type { DashboardCommercialDiscussionClientV2Dto } from '@/lib/api/v2/dashboard'

function getMilestoneColorClasses(status?: string | null): string {
  const statusLower = (status || '').toLowerCase()

  switch (statusLower) {
    case 'danger':
      return 'bg-red-100 text-red-600 hover:bg-red-100'
    case 'active':
      // A fill, like every sibling status in this map. The outlined version introduced with the
      // ink theme was white on a white card — and its `border border-slate-300` never rendered at
      // all, because both call sites pass `border-0` on the Badge and that wins in the emitted
      // stylesheet. So the one status meaning « the client is HERE right now » had literally no
      // boundary of any kind.
      //
      // slate-200 + slate-900, and this is a compromise between two findings that are BOTH right.
      // A first pass tried solid ink and the browser QA showed why 432f36a had moved away from it:
      // twenty black pills in a card grid outweigh the amber profile warning above them, which is
      // the one thing on this page that is supposed to shout. But a neutral chip cannot reach the
      // 3:1 a component boundary wants without going dark, and dark is what that commit rejected.
      // So: one clear step above `default` (bg-slate-100 text-slate-600), separated by fill AND by
      // text weight, quiet enough not to dominate. The real answer is a hue this map does not
      // spend yet — that is a design decision, not a review fix.
      return 'bg-slate-200 text-slate-900 hover:bg-slate-200'
    case 'info':
      return 'bg-sky-100 text-sky-600 hover:bg-sky-100'
    case 'green':
      return 'bg-green-100 text-green-600 hover:bg-green-100'
    // case 'warn':
    //   return 'bg-amber-100 text-amber-600 hover:bg-amber-100'
    default:
      return 'bg-slate-100 text-slate-600 hover:bg-slate-100'
  }
}

/**
 * SOLY-REGIE-006 — an intervention and a job read identically in this list, and a 172 € retouche
 * beside a 25 000 € cuisine is exactly the pair that needs telling apart at a glance. Quiet on
 * purpose: it labels the row, it does not compete with the amount.
 */
function InterventionBadge({ show }: { show: boolean }) {
  if (!show) return null
  return (
    <Badge variant="secondary" className="shrink-0 rounded-full font-normal">
      Intervention
    </Badge>
  )
}

function SignedProjectsView({
  items,
  totalCount,
  page,
  pageSize,
  onPageChange,
  isLoading,
  sort,
  onSortChange,
}: {
  items: Array<{
    clientId: string
    clientName: string | null
    projects: Array<{
      projectId: string
      projectReference: string | null
      projectName: string | null
      projectTypeLabel: string | null
      quoteSignedAt: string | null
      invoicingPercentage: number | null
      invoicingStatus: string | null
      salesOwnerName: string | null
      orderId: string
      totalSaleAmount: number | null
      salesOrderUpdatedDate: string | null
      desiredPlacementDate: string | null
      placementDateStatus: string | null
      isIntervention: boolean
    }> | null
  }>
  totalCount: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  isLoading: boolean
  sort: {
    key: 'quoteSignedAt' | 'salesOwnerName' | 'invoicingPercentage'
    dir: 'asc' | 'desc'
  }
  onSortChange: (next: { key: 'quoteSignedAt' | 'salesOwnerName' | 'invoicingPercentage'; dir: 'asc' | 'desc' }) => void
}) {
  const navigate = useNavigate()
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, totalCount)

  const getInvoicingBadgeClassName = (status?: string | null) => {
    const s = (status || '').toLowerCase()

    switch (s) {
      case 'danger':
        return 'rounded-md bg-rose-500/15 text-rose-700 dark:text-rose-300'
      case 'active':
        return 'rounded-md bg-sky-500/15 text-sky-700 dark:text-sky-300'
      case 'info':
        return 'rounded-md bg-sky-500/15 text-sky-700 dark:text-sky-300'
      case 'green':
      case 'success':
        return 'rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
      case 'warn':
      case 'warning':
        return 'rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-300'
      default:
        return 'rounded-md bg-slate-500/10 text-slate-700 dark:text-slate-300'
    }
  }

  const getPlacementDateBadgeClassName = (status: string | null) => {
    switch (status) {
      case 'danger': return 'rounded-md bg-rose-500/15 text-rose-700 dark:text-rose-300'
      case 'warn':   return 'rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-300'
      case 'info':   return 'rounded-md bg-sky-500/15 text-sky-700 dark:text-sky-300'
      default:       return 'rounded-md bg-slate-500/10 text-slate-700 dark:text-slate-300'
    }
  }

  const formatPlacementDate = (iso: string) => {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' })
  }

  const formatSignedAtRelative = (iso: string) => {
    const signedAt = new Date(iso)
    if (Number.isNaN(signedAt.getTime())) return '—'

    const now = new Date()
    const diffMs = now.getTime() - signedAt.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays <= 0) return "Aujourd'hui"
    if (diffDays === 1) return 'Hier'
    if (diffDays < 7) return `Il y a ${diffDays} j`

    const diffWeeks = Math.floor(diffDays / 7)
    if (diffWeeks < 5) return `Il y a ${diffWeeks} sem`

    const diffMonths = Math.floor(diffDays / 30)
    if (diffMonths < 12) return `Il y a ${diffMonths} mois`

    const diffYears = Math.floor(diffDays / 365)
    return `Il y a ${diffYears} an${diffYears > 1 ? 's' : ''}`
  }

  const toggleSort = (key: typeof sort.key) => {
    if (sort.key !== key) {
      onSortChange({ key, dir: 'desc' })
      return
    }
    onSortChange({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' })
  }

  const sortIndicator = (key: typeof sort.key) => {
    if (sort.key !== key) return ''
    return sort.dir === 'asc' ? ' ↑' : ' ↓'
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Projets signés</h2>
          <p className="text-sm text-muted-foreground">
            {totalCount} client{totalCount > 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <div className="text-xs text-muted-foreground">
            Affichage {start}-{end} sur {totalCount}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              disabled={isLoading || page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              Précédent
            </Button>
            <div className="text-xs text-muted-foreground">
              Page {page}/{totalPages}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              disabled={isLoading || page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Suivant
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Trier par:</span>
        <Button
          type="button"
          variant={sort.key === 'quoteSignedAt' ? 'default' : 'outline'}
          size="sm"
          className="h-7 text-xs"
          onClick={() => toggleSort('quoteSignedAt')}
        >
          Date acceptation{sortIndicator('quoteSignedAt')}
        </Button>
        <Button
          type="button"
          variant={sort.key === 'invoicingPercentage' ? 'default' : 'outline'}
          size="sm"
          className="h-7 text-xs"
          onClick={() => toggleSort('invoicingPercentage')}
        >
          Facturation{sortIndicator('invoicingPercentage')}
        </Button>
        <Button
          type="button"
          variant={sort.key === 'salesOwnerName' ? 'default' : 'outline'}
          size="sm"
          className="h-7 text-xs"
          onClick={() => toggleSort('salesOwnerName')}
        >
          Commercial{sortIndicator('salesOwnerName')}
        </Button>
      </div>

      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-lg">
            <div className="rounded-md border bg-background px-3 py-2 text-xs text-muted-foreground shadow-sm">
              Chargement…
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <div className="rounded-lg border bg-background py-10 text-center text-sm text-muted-foreground">
            Aucun client signé
          </div>
        ) : (
          <div className="space-y-1.5">
            {items.map((client) => {
              const projects = client.projects || []
              const isSingleProject = projects.length === 1
              const singleProject = isSingleProject ? projects[0] : null

              if (isSingleProject && singleProject) {
                return (
                  <div
                    key={client.clientId}
                    onClick={() => navigate(`/clients/${client.clientId}`)}
                    className="rounded-lg border bg-background hover:bg-muted/30 transition-colors cursor-pointer overflow-x-auto"
                  >
                    <div className="p-2.5 flex items-center gap-4 min-w-max">
                      <div className="w-[280px] flex-shrink-0">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-semibold text-base truncate">{client.clientName || '—'}</h3>
                          
                        </div>
                        {/* Same row as the project name, like the multi-project branch two blocks
                            down. Left as a bare sibling it dropped onto a third line and grew the
                            row — one badge, one meaning, two positions on the same screen. */}
                        <div className="flex items-center gap-1.5">
                          <p className="min-w-0 truncate text-sm text-muted-foreground">
                            {singleProject.projectName?.trim() || singleProject.projectTypeLabel || '—'}
                          </p>
                          <InterventionBadge show={singleProject.isIntervention} />
                        </div>
                      </div>

                      <div className="flex items-center gap-6 flex-1">
                        <div className="min-w-[100px]">
                          <div className="text-xs text-muted-foreground">Accepté</div>
                          <div className="font-medium text-sm">
                            {singleProject.quoteSignedAt ? formatSignedAtRelative(singleProject.quoteSignedAt) : '—'}
                          </div>
                        </div>
                        <div className="min-w-[100px]">
                          <div className="text-xs text-muted-foreground">Facturation</div>
                          <div className="text-sm">
                            {singleProject.invoicingPercentage == null ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <Badge className={getInvoicingBadgeClassName(singleProject.invoicingStatus)}>
                                {singleProject.invoicingPercentage}%
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="min-w-[120px]">
                          <div className="text-xs text-muted-foreground">Commercial</div>
                          <div className="text-sm text-muted-foreground truncate">{singleProject.salesOwnerName || '—'}</div>
                        </div>
                        <div className="min-w-[110px]">
                          <div className="text-xs text-muted-foreground">Placement</div>
                          <div className="text-sm">
                            {singleProject.placementDateStatus ? (
                              <Badge className={getPlacementDateBadgeClassName(singleProject.placementDateStatus)}>
                                <Calendar className="w-3 h-3" />
                                {singleProject.desiredPlacementDate
                                  ? formatPlacementDate(singleProject.desiredPlacementDate)
                                  : 'À définir'}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                        </div>
                        {/* TODO: Uncomment when backend provides supplier order status/date
                        <div className="min-w-[120px]">
                          <div className="text-xs text-muted-foreground">Commande fournisseur</div>
                          <div className="text-sm text-muted-foreground">—</div>
                        </div>
                        */}
                      </div>

                      {singleProject.totalSaleAmount != null && (
                        <div className="text-base font-semibold shrink-0 ml-auto">
                          {formatCurrency(singleProject.totalSaleAmount)}
                        </div>
                      )}
                    </div>
                  </div>
                )
              }

              return (
                <div key={client.clientId} className="rounded-lg border bg-background">
                  <div
                    onClick={() => navigate(`/clients/${client.clientId}`)}
                    className="px-2.5 py-2 sm:px-3 hover:bg-muted/20 transition-colors cursor-pointer border-b"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-base">{client.clientName || '—'}</h3>
                      <Badge className="rounded-full bg-muted text-muted-foreground">
                        {projects.length} projet{projects.length > 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </div>

                  <div>
                    {projects.map((project, index) => (
                      <div
                        key={project.projectId}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/projects/${project.projectId}`)
                        }}
                        className={`p-2.5 hover:bg-muted/30 transition-colors cursor-pointer overflow-x-auto ${
                          index > 0 ? 'border-t border-muted-foreground/10' : ''
                        }`}
                      >
                        <div className="flex items-center gap-4 min-w-max">
                          <div className="w-[280px] flex-shrink-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-sm truncate">{project.projectName?.trim() || project.projectTypeLabel || '—'}</span>
                              <InterventionBadge show={project.isIntervention} />
                              
                            </div>
                            {project.projectReference && (
                              <p className="text-xs text-muted-foreground">{project.projectReference}</p>
                            )}
                          </div>

                          <div className="flex items-center gap-6 flex-1">
                            <div className="min-w-[100px]">
                              <div className="text-xs text-muted-foreground">Accepté</div>
                              <div className="font-medium text-sm">
                                {project.quoteSignedAt ? formatSignedAtRelative(project.quoteSignedAt) : '—'}
                              </div>
                            </div>
                            <div className="min-w-[100px]">
                              <div className="text-xs text-muted-foreground">Facturation</div>
                              <div className="text-sm">
                                {project.invoicingPercentage == null ? (
                                  <span className="text-muted-foreground">—</span>
                                ) : (
                                  <Badge className={getInvoicingBadgeClassName(project.invoicingStatus)}>
                                    {project.invoicingPercentage}%
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="min-w-[120px]">
                              <div className="text-xs text-muted-foreground">Commercial</div>
                              <div className="text-sm text-muted-foreground truncate">{project.salesOwnerName || '—'}</div>
                            </div>
                            <div className="min-w-[110px]">
                              <div className="text-xs text-muted-foreground">Placement</div>
                              <div className="text-sm">
                                {project.placementDateStatus ? (
                                  <Badge className={getPlacementDateBadgeClassName(project.placementDateStatus)}>
                                    <Calendar className="w-3 h-3" />
                                    {project.desiredPlacementDate
                                      ? formatPlacementDate(project.desiredPlacementDate)
                                      : 'À définir'}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </div>
                            </div>
                            {/* TODO: Uncomment when backend provides supplier order status/date
                            <div className="min-w-[120px]">
                              <div className="text-xs text-muted-foreground">Commande fournisseur</div>
                              <div className="text-sm text-muted-foreground">—</div>
                            </div>
                            */}
                          </div>

                          {project.totalSaleAmount != null && (
                            <div className="text-base font-semibold shrink-0 ml-auto">
                              {formatCurrency(project.totalSaleAmount)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default function HomePage() {
  const { user } = useAuth()
  const [page, setPage] = useState(1)
  const pageSize = 25
  const [signedProjectsSort, setSignedProjectsSort] = useState<{
    key: 'quoteSignedAt' | 'salesOwnerName' | 'invoicingPercentage'
    dir: 'asc' | 'desc'
  }>({
    key: 'quoteSignedAt',
    dir: 'desc',
  })

  const [commercialDiscussionFilter, setCommercialDiscussionFilter] = useState<
    'all' | 'no_quote' | 'quote_draft' | 'quote_issued'
  >('all')
  const commercialDiscussionFilterTouchedRef = useRef(false)

  const commercialDiscussionState: 'no_quote' | 'quote_draft' | 'quote_issued' | undefined =
    commercialDiscussionFilter === 'all' ? undefined : commercialDiscussionFilter

  const {
    data: headerData,
    isLoading: isHeaderLoading,
    error: headerError,
    refetch: refetchHeader,
    commercialDiscussionCounts,
  } = useDashboardHomeHeaderV2({ state: commercialDiscussionState })

  const {
    data: signedProjectsData,
    isLoading: isSignedProjectsLoading,
    error: signedProjectsError,
    refetch: refetchSignedProjects,
  } = useDashboardSignedProjectsV2({
    page,
    pageSize,
    orderBy: signedProjectsSort.key,
    orderDir: signedProjectsSort.dir,
  })

  const {
    items: interventions,
    hasMore: hasMoreInterventions,
    isLoading: isInterventionsLoading,
    error: interventionsError,
    refetch: refetchInterventions,
  } = useDashboardInterventionsV2()

  const [clientSearch, setClientSearch] = useState('')
  const { results: clientSearchResults, isLoading: isClientSearchLoading } = useClientSearchV2({
    q: clientSearch,
    take: 8,
  })

  const greeting = useMemo(() => {
    const displayName = user?.displayName || ''
    const greetingText = headerData?.greeting.greeting || 'Bonjour'
    return `${greetingText} ${displayName} 👋`
  }, [user?.displayName, headerData?.greeting.greeting])

  const commercialDiscussionItems = headerData?.commercialDiscussionClients || []

  // SOLY-SALES-001 mini-scope 03 — single active dialog at a time (follow-up OR history).
  // Stores the snapshot needed to render the dialog header (client name + envoyé date)
  // so the dialog stays consistent even if the underlying list reorders after a refresh.
  const [activeFollowUpDialog, setActiveFollowUpDialog] = useState<{
    type: 'log' | 'history'
    projectId: string
    clientName: string
    issuedAt: string | null
  } | null>(null)

  // Group quote_issued items by urgencyBucket. Only meaningful when the filter is active.
  // Items missing a bucket (defensive — shouldn't happen for QuoteIssued state) fall into 'fresh'.
  // Archived items are surfaced in their own collapsible section (mini-scope 04).
  const groupedByBucket = useMemo(() => {
    const groups: Record<
      'fresh' | 'warming' | 'critical' | 'archived',
      DashboardCommercialDiscussionClientV2Dto[]
    > = {
      fresh: [],
      warming: [],
      critical: [],
      archived: [],
    }
    if (commercialDiscussionFilter !== 'quote_issued') return groups
    for (const item of commercialDiscussionItems) {
      const bucket = item.urgencyBucket
      if (bucket === 'warming') groups.warming.push(item)
      else if (bucket === 'critical') groups.critical.push(item)
      else if (bucket === 'archived') groups.archived.push(item)
      else groups.fresh.push(item)
    }
    return groups
  }, [commercialDiscussionItems, commercialDiscussionFilter])

  useEffect(() => {
    if (commercialDiscussionFilterTouchedRef.current) return
    if (!commercialDiscussionCounts) return

    const nextFilter: 'all' | 'no_quote' | 'quote_draft' =
      commercialDiscussionCounts.quoteDraft > 0
        ? 'quote_draft'
        : commercialDiscussionCounts.noQuote > 0
          ? 'no_quote'
          : 'all'

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCommercialDiscussionFilter(nextFilter)
  }, [commercialDiscussionCounts])

  const dashboardError = headerError || signedProjectsError

  const handleRetryDashboard = () => {
    refetchHeader()
    refetchSignedProjects()
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <TopBar />
      <main className="flex-1 overflow-y-auto bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-8 lg:px-12 space-y-6">
          <IncompleteProfileBanner />
          <div className="relative rounded-2xl border bg-background">
            <div className="pointer-events-none rounded-2xl absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-sky-500/10" />
            <div className="relative p-4 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{greeting}</h1>
                  <p className="text-sm text-muted-foreground">{headerData?.greeting.dateLabel || ''}</p>
                </div>

                <div className="w-full sm:max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setClientSearch('')
                        }
                      }}
                      placeholder="Rechercher un client…"
                      className="pl-9 bg-background/70"
                    />

                    {clientSearch.trim() && (
                      <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-30 overflow-hidden rounded-lg border bg-background shadow-md">
                        {isClientSearchLoading ? (
                          <div className="p-3 text-sm text-muted-foreground">Recherche…</div>
                        ) : clientSearchResults.length === 0 ? (
                          <div className="p-3 text-sm text-muted-foreground">Aucun client trouvé</div>
                        ) : (
                          <div className="max-h-72 overflow-y-auto">
                            {clientSearchResults
                              .map((c) => {
                                const customerId = c.customerId || ''
                                const name = c.name || ''
                                if (!customerId || !name) return null

                                const primaryContact = (c.contacts || []).find((x) => x?.isPrimary) || (c.contacts || [])[0] || null
                                const phone = primaryContact?.phone || ''

                                const lastJobSite = (c.lastJobSites || [])[0] || null
                                const jobSiteLabel = lastJobSite?.label || ''
                                const jobSiteAddress = lastJobSite?.address || ''

                                const identifiers = c.vatNumber || c.companyRegistrationNumber || c.glnNumber || ''

                                return (
                                  <Link
                                    key={customerId}
                                    to={`/clients/${customerId}`}
                                    onClick={() => setClientSearch('')}
                                    className="block px-3 py-2 hover:bg-muted/50"
                                  >
                                    <div className="truncate text-sm font-medium text-foreground">{name}</div>
                                    {phone ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{phone}</div> : null}
                                    {jobSiteLabel || jobSiteAddress ? (
                                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                                        {jobSiteLabel ? jobSiteLabel : 'Chantier'}
                                        {jobSiteAddress ? ` · ${jobSiteAddress}` : ''}
                                      </div>
                                    ) : null}
                                    {!phone && !jobSiteLabel && !jobSiteAddress && identifiers ? (
                                      <div className="mt-0.5 truncate text-xs text-muted-foreground">{identifiers}</div>
                                    ) : null}
                                  </Link>
                                )
                              })
                              .filter(Boolean)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border bg-gradient-to-br from-emerald-500/10 via-background to-background p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">Clients actifs</div>
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                  <Users className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-1 text-2xl font-bold">3</div>
            </div>
            <div className="rounded-lg border bg-gradient-to-br from-sky-500/10 via-background to-background p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">Leads à contacter</div>
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-sky-500/15 text-sky-700 dark:text-sky-300">
                  <UserCheck className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-1 text-2xl font-bold">3</div>
            </div>
            <div className="rounded-lg border bg-gradient-to-br from-violet-500/10 via-background to-background p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">Projets en cours</div>
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-violet-500/15 text-violet-700 dark:text-violet-300">
                  <Briefcase className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-1 text-2xl font-bold">8</div>
            </div>
            <div className="rounded-lg border bg-gradient-to-br from-amber-500/10 via-background to-background p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">À facturer</div>
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-300">
                  <Receipt className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-1 text-2xl font-bold">5</div>
            </div>
          </div> */}

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">Vos dossiers en cours</h2>
                  <Badge className="rounded-full bg-sky-500/15 text-sky-700 dark:text-sky-300 hover:bg-sky-500/20">
                    {commercialDiscussionCounts?.all ?? commercialDiscussionItems.length}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">À faire une offre ou à relancer</p>
              </div>

              {/* SOLY-REGIE-001 — two doors, named by the intention, with EQUAL weight. The real
                  ratio is ~50 chantiers a year against ~600 interventions: giving the right-hand
                  door less room would misrepresent the office's day. */}
              <div className="flex shrink-0 items-center gap-2">
                <WinnerImportButton testId="home-winner-import-btn" />
                <Button asChild variant="outline" size="default" data-testid="home-new-project-btn" className="gap-2">
                  <Link to="/projects/new">
                    <Plus className="h-4 w-4" />
                    Nouveau chantier
                  </Link>
                </Button>
                {/* Gated on the SAME flag as the route it leads to. Without the planning module the
                    execution is never provisioned: the file would exist with its client, its job
                    site and its VAT, invisible to the Inbox forever, and the user would land on a
                    silent redirect. A door that leads nowhere must not be drawn. */}
                {user?.features?.planning && (
                  <Button asChild variant="default" size="default" data-testid="home-new-intervention-btn" className="gap-2">
                    <Link to="/interventions/new">
                      <Plus className="h-4 w-4" />
                      Nouvelle intervention
                    </Link>
                  </Button>
                )}
              </div>
            </div>

            {!isHeaderLoading && !headerError ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant={commercialDiscussionFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    commercialDiscussionFilterTouchedRef.current = true
                    setCommercialDiscussionFilter('all')
                  }}
                >
                  Tous
                  <span className="ml-2 text-xs opacity-80">{commercialDiscussionCounts?.all ?? 0}</span>
                </Button>
                <Button
                  type="button"
                  variant={commercialDiscussionFilter === 'no_quote' ? 'default' : 'outline'}
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    commercialDiscussionFilterTouchedRef.current = true
                    setCommercialDiscussionFilter('no_quote')
                  }}
                >
                  Nouveau projet
                  <span className="ml-2 text-xs opacity-80">{commercialDiscussionCounts?.noQuote ?? 0}</span>
                </Button>
                <Button
                  type="button"
                  variant={commercialDiscussionFilter === 'quote_draft' ? 'default' : 'outline'}
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    commercialDiscussionFilterTouchedRef.current = true
                    setCommercialDiscussionFilter('quote_draft')
                  }}
                >
                  Devis en cours
                  <span className="ml-2 text-xs opacity-80">{commercialDiscussionCounts?.quoteDraft ?? 0}</span>
                </Button>
                <Button
                  type="button"
                  variant={commercialDiscussionFilter === 'quote_issued' ? 'default' : 'outline'}
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    commercialDiscussionFilterTouchedRef.current = true
                    setCommercialDiscussionFilter('quote_issued')
                  }}
                >
                  Devis envoyé
                  <span className="ml-2 text-xs opacity-80">{commercialDiscussionCounts?.quoteIssued ?? 0}</span>
                </Button>
              </div>
            ) : null}

            {isHeaderLoading ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="rounded-lg border bg-background p-4">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="mt-2 h-3 w-56" />
                    <Skeleton className="mt-3 h-7 w-28" />
                  </div>
                ))}
              </div>
            ) : headerError ? (
              <div className="rounded-lg border bg-background p-4">
                <div className="text-sm font-medium">Erreur de chargement</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Impossible de charger les dossiers en cours.
                </div>
                <div className="mt-3">
                  <Button type="button" variant="outline" size="sm" onClick={() => refetchHeader()}>
                    Réessayer
                  </Button>
                </div>
              </div>
            ) : commercialDiscussionItems.length === 0 ||
              (commercialDiscussionFilter === 'quote_issued' &&
                groupedByBucket.fresh.length +
                  groupedByBucket.warming.length +
                  groupedByBucket.critical.length +
                  groupedByBucket.archived.length ===
                  0) ? (
              <div className="rounded-lg border bg-background p-6 text-sm text-muted-foreground">
                {commercialDiscussionFilter === 'quote_issued' ? (
                  <>
                    <div className="font-medium text-foreground">
                      Pas de devis envoyé en attente
                    </div>
                    <div className="mt-1">Tout est sous contrôle.</div>
                  </>
                ) : (
                  'Aucun dossier dans ce filtre.'
                )}
              </div>
            ) : commercialDiscussionFilter === 'quote_issued' ? (
              <div className="space-y-6" data-testid="commercial-discussion-grouped">
                {(['fresh', 'warming', 'critical'] as const).map((bucket) => {
                  const items = groupedByBucket[bucket]
                  if (items.length === 0) return null
                  return (
                    <UrgencyBucketSection key={bucket} bucket={bucket} count={items.length}>
                      {items.map((c) => (
                        <CommercialDiscussionCardIssued
                          key={c.customerId}
                          item={c}
                          onLogFollowUp={() =>
                            c.projectId &&
                            setActiveFollowUpDialog({
                              type: 'log',
                              projectId: c.projectId,
                              clientName: c.name || '—',
                              issuedAt: c.issuedAt,
                            })
                          }
                          onShowHistory={() =>
                            c.projectId &&
                            setActiveFollowUpDialog({
                              type: 'history',
                              projectId: c.projectId,
                              clientName: c.name || '—',
                              issuedAt: c.issuedAt,
                            })
                          }
                        />
                      ))}
                    </UrgencyBucketSection>
                  )
                })}
                <ArchivedSection
                  items={groupedByBucket.archived}
                  onShowHistory={(c) =>
                    c.projectId &&
                    setActiveFollowUpDialog({
                      type: 'history',
                      projectId: c.projectId,
                      clientName: c.name || '—',
                      issuedAt: c.issuedAt,
                    })
                  }
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {commercialDiscussionItems.map((c) => (
                  <Link
                    key={c.customerId}
                    to={`/clients/${c.customerId}`}
                    className="block rounded-lg border bg-gradient-to-br from-muted/10 via-background to-background p-4 hover:bg-muted/20"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{c.name || '—'}</div>
                      </div>

                      {c.preferredLanguageLabel && (
                        <div className="shrink-0">
                          <Badge variant="outline" className="rounded-md">
                            {c.preferredLanguageLabel}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {(c.phone || c.email) && (
                      <div className="mt-3 space-y-1">
                        {c.phone && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Phone className="h-3.5 w-3.5" />
                            <span className="truncate">{c.phone}</span>
                          </div>
                        )}
                        {c.email && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Mail className="h-3.5 w-3.5" />
                            <span className="truncate">{c.email}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {(c.salesOwner?.initials || c.salesOwner?.fullName || (c.milestones || []).length > 0) && (
                      <div className="mt-3 flex items-end justify-between gap-3">
                        <div className="min-w-0">
                          {(c.salesOwner?.initials || c.salesOwner?.fullName) && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {c.salesOwner?.initials && (
                                <Badge variant="outline" className="rounded-full px-2">
                                  {c.salesOwner.initials}
                                </Badge>
                              )}
                              {c.salesOwner?.fullName && <span className="truncate">{c.salesOwner.fullName}</span>}
                            </div>
                          )}
                        </div>

                        {(c.milestones || []).length > 0 && (
                          <div className="flex flex-wrap items-center justify-end gap-2 text-right">
                            {c.totalProjectsCount != null && (
                              <span className="text-xs text-muted-foreground">
                                {c.totalProjectsCount} projet{c.totalProjectsCount > 1 ? 's' : ''}
                              </span>
                            )}
                            {(c.milestones || []).slice(0, 4).map((m, idx) => {
                              const colorClasses = getMilestoneColorClasses(m.status)
                              const display = m.label || m.status || '—'
                              return (
                                <span key={`${m.label || ''}-${idx}`} className="inline-flex items-center">
                                  <Badge variant="outline" className={`rounded-md border-0 ${colorClasses}`}>
                                    {display}
                                  </Badge>
                                </span>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* SOLY-REGIE-006 — the interventions the office still owes something to. Between the
              commercial discussions above and the sold files below, because that is where it sits
              in the life of a file: opened, not yet sold or not yet dated. It renders nothing at
              all when there is nothing to do. */}
          <InterventionsSection
            items={interventions}
            hasMore={hasMoreInterventions}
            isLoading={isInterventionsLoading}
            error={interventionsError}
            onRetry={refetchInterventions}
          />

          {signedProjectsError ? (
            <div className="rounded-lg border bg-background p-4">
              <div className="text-sm font-medium">Erreur de chargement</div>
              <div className="mt-1 text-sm text-muted-foreground">
                Impossible de charger les projets signés.
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => refetchSignedProjects()}>
                  Réessayer
                </Button>
                {dashboardError ? (
                  <Button type="button" variant="ghost" size="sm" onClick={handleRetryDashboard}>
                    Tout recharger
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <SignedProjectsView
              items={signedProjectsData?.items || []}
              totalCount={signedProjectsData?.totalCount || 0}
              page={signedProjectsData?.page || page}
              pageSize={signedProjectsData?.pageSize || pageSize}
              onPageChange={(p) => setPage(p)}
              isLoading={isSignedProjectsLoading}
              sort={signedProjectsSort}
              onSortChange={setSignedProjectsSort}
            />
          )}

          {/* SOLY-ORDER-002 MS-04 — archived projects (collapsed, hidden when empty) */}
          <div className="mt-6">
            <DeletedProjectsSection />
          </div>
        </div>
      </main>

      {/* SOLY-SALES-001 mini-scope 03 — Sales Cockpit follow-up dialogs.
          Single active dialog at a time (log OR history). Mounted at page level so
          they render as portals above the dashboard content. */}
      {activeFollowUpDialog?.type === 'log' && (
        <FollowUpDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setActiveFollowUpDialog(null)
          }}
          projectId={activeFollowUpDialog.projectId}
          clientName={activeFollowUpDialog.clientName}
          issuedAtLabel={
            activeFollowUpDialog.issuedAt
              ? new Intl.DateTimeFormat('fr-BE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                }).format(new Date(activeFollowUpDialog.issuedAt))
              : null
          }
          onSuccess={() => {
            setActiveFollowUpDialog(null)
            refetchHeader()
          }}
        />
      )}
      {activeFollowUpDialog?.type === 'history' && (
        <FollowUpHistoryDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setActiveFollowUpDialog(null)
          }}
          projectId={activeFollowUpDialog.projectId}
          clientName={activeFollowUpDialog.clientName}
        />
      )}
    </div>
  )
}
